import { hasOpenSaveBarrierForAttempt } from "./saveBarrier";
import type { SavePersistedEventDetail, SaveFailedEventDetail } from "./saveEvents";

export const SPACE_ADD_PERSIST_BARRIER_CHANGED_EVENT =
  "viewr:spaceAddPersistBarrierChanged";

export type SpaceAddPersistBarrierStatus =
  | "idle"
  | "awaiting_canonical"
  | "awaiting_persist"
  | "persisted"
  | "failed";

export type SpaceAddPersistBarrierSnapshot = {
  status: SpaceAddPersistBarrierStatus;
  transactionId: string | null;
  targetSpaceIndex: number | null;
  saveAttemptId: string | null;
  error: string | null;
  updatedAt: number | null;
};

const IDLE_SNAPSHOT: SpaceAddPersistBarrierSnapshot = {
  status: "idle",
  transactionId: null,
  targetSpaceIndex: null,
  saveAttemptId: null,
  error: null,
  updatedAt: null,
};

let snapshot: SpaceAddPersistBarrierSnapshot = IDLE_SNAPSHOT;

function publish(): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(
    new CustomEvent(SPACE_ADD_PERSIST_BARRIER_CHANGED_EVENT, {
      detail: snapshot,
    }),
  );
}

function update(
  patch: Partial<SpaceAddPersistBarrierSnapshot>,
): SpaceAddPersistBarrierSnapshot {
  snapshot = {
    ...snapshot,
    ...patch,
    updatedAt: Date.now(),
  };
  publish();
  return snapshot;
}

export function getSpaceAddPersistBarrierSnapshot(): SpaceAddPersistBarrierSnapshot {
  return snapshot;
}

/**
 * 저장 job이 살아 있다는 신호 없이 barrier가 진행 중 상태로 머물 수 있는 시간.
 *
 * Add Space delta가 실패하거나 응답이 끊기면 canonical/persist 단계가 끝나지
 * 않아 barrier가 진행 중 상태로 남는다. 그러면 해당 Space에서 Import가 영구히
 * 비활성화되므로, 이 시간을 넘기면 차단을 해제한다.
 *
 * 단 이것은 절대 시한이 아니라 **무활동 시간**이다. bind된 save attempt의 저장
 * job이 살아 있는 동안은 계속 활동으로 간주해 차단을 유지한다. `.viewr` 번들
 * 재패키징(STUDIO-SPACE-003 ④ 구간)은 목표 상한이 정해져 있지 않아 고정 시한으로
 * 끊으면 저장이 정상 진행 중인데도 Import가 실패한다.
 */
export const SPACE_ADD_PERSIST_BARRIER_STALE_MS = 30_000;

/**
 * 저장 job 생존 신호가 계속 오더라도 대기를 끝내는 최후 상한.
 *
 * `resolveSaveBarrier`가 끝내 호출되지 않는 경우에 대비한 안전장치일 뿐이며,
 * 정상 경로에서는 도달하지 않는다.
 */
export const SPACE_ADD_PERSIST_BARRIER_MAX_WAIT_MS = 300_000;

/** barrier 대기 중 저장 job 생존을 다시 확인하는 주기. */
export const SPACE_ADD_PERSIST_BARRIER_POLL_MS = 1_000;

/**
 * bind된 save attempt의 저장 job이 아직 진행 중인지.
 *
 * `awaiting_canonical` 단계에는 아직 save attempt가 없으므로 항상 false다.
 */
export function isSpaceAddPersistSaveAlive(
  value: SpaceAddPersistBarrierSnapshot = snapshot,
): boolean {
  return (
    value.status === "awaiting_persist" &&
    hasOpenSaveBarrierForAttempt(value.saveAttemptId)
  );
}

function isBarrierStale(
  value: SpaceAddPersistBarrierSnapshot,
  now: number,
): boolean {
  if (isSpaceAddPersistSaveAlive(value)) {
    return false;
  }
  return (
    typeof value.updatedAt === "number" &&
    now - value.updatedAt >= SPACE_ADD_PERSIST_BARRIER_STALE_MS
  );
}

export function isSpaceAddPersistBarrierBlocking(
  value: SpaceAddPersistBarrierSnapshot = snapshot,
  now: number = Date.now(),
): boolean {
  // 실패는 더 이상 차단하지 않는다. Add Space가 실패했다고 해서 그 Space의
  // Import를 영구히 막으면 사용자가 복구할 방법이 없다. 실패 자체는
  // waitForSpaceAddPersistBarrier가 reject로 알린다.
  if (value.status !== "awaiting_canonical" && value.status !== "awaiting_persist") {
    return false;
  }

  return !isBarrierStale(value, now);
}

export function isSpaceAddPersistBarrierBlockingForTarget(
  targetSpaceIndex: number | null | undefined,
  value: SpaceAddPersistBarrierSnapshot = snapshot,
  now: number = Date.now(),
): boolean {
  return (
    typeof targetSpaceIndex === "number" &&
    value.targetSpaceIndex === targetSpaceIndex &&
    isSpaceAddPersistBarrierBlocking(value, now)
  );
}

export function beginSpaceAddPersistBarrier(args: {
  transactionId: string;
  targetSpaceIndex: number;
}): SpaceAddPersistBarrierSnapshot {
  return update({
    status: "awaiting_canonical",
    transactionId: args.transactionId,
    targetSpaceIndex: args.targetSpaceIndex,
    saveAttemptId: null,
    error: null,
  });
}

export function bindSpaceAddPersistSaveAttempt(args: {
  transactionId: string;
  targetSpaceIndex: number;
  saveAttemptId: string;
}): boolean {
  const saveAttemptId = args.saveAttemptId.trim();
  if (
    !saveAttemptId ||
    snapshot.transactionId !== args.transactionId ||
    snapshot.targetSpaceIndex !== args.targetSpaceIndex
  ) {
    return false;
  }

  update({
    status: "awaiting_persist",
    saveAttemptId,
    error: null,
  });
  return true;
}

export function markSpaceAddPersisted(
  detail: SavePersistedEventDetail,
): boolean {
  const persistedSaveAttemptId = detail.saveAttemptId?.trim();
  if (
    snapshot.status !== "awaiting_persist" ||
    !persistedSaveAttemptId ||
    persistedSaveAttemptId !== snapshot.saveAttemptId
  ) {
    return false;
  }

  update({ status: "persisted", error: null });
  return true;
}

export function markSpaceAddPersistFailed(
  detail: SaveFailedEventDetail,
): boolean {
  const failedSaveAttemptId = detail.saveAttemptId?.trim();
  if (
    snapshot.status !== "awaiting_persist" ||
    !failedSaveAttemptId ||
    failedSaveAttemptId !== snapshot.saveAttemptId
  ) {
    return false;
  }

  update({
    status: "failed",
    error: detail.error ?? detail.reason ?? "space_add_persist_failed",
  });
  return true;
}

export function markSpaceAddPersistFailedForTransaction(
  transactionId: string,
  error: string,
): boolean {
  if (snapshot.transactionId !== transactionId) return false;
  update({ status: "failed", error });
  return true;
}

export function resetSpaceAddPersistBarrierForTests(): void {
  snapshot = IDLE_SNAPSHOT;
}

export function clearSpaceAddPersistBarrier(): void {
  if (snapshot.status === "idle") return;
  snapshot = IDLE_SNAPSHOT;
  publish();
}

/**
 * Add Space persist가 끝날 때까지 대기한다.
 *
 * 종료 조건 (STUDIO-SPACE-003):
 * - `persisted` → resolve. attach 제출 가능.
 * - `failed` / 대상 Space 불일치 → reject.
 * - 저장 job 생존 신호 없이 무활동이 `staleMs`를 넘김 → resolve. 차단만 해제하고
 *   진행시킨다. 저장이 끝났는데 통지를 놓쳤거나 애초에 시작되지 않은 경우다.
 * - `maxWaitMs` 초과 → reject. 정상 경로에서는 도달하지 않는 안전장치.
 *
 * 저장 job이 살아 있는 동안은 시한을 두지 않는다. `.viewr` 재패키징 시간에
 * 상한이 없어 고정 시한으로 끊으면 정상 저장 중에도 Import가 실패한다.
 */
export function waitForSpaceAddPersistBarrier(args: {
  targetSpaceIndex: number;
  maxWaitMs?: number;
  staleMs?: number;
  pollIntervalMs?: number;
  signal?: AbortSignal;
}): Promise<SpaceAddPersistBarrierSnapshot> {
  const maxWaitMs = args.maxWaitMs ?? SPACE_ADD_PERSIST_BARRIER_MAX_WAIT_MS;
  const staleMs = args.staleMs ?? SPACE_ADD_PERSIST_BARRIER_STALE_MS;
  const pollIntervalMs =
    args.pollIntervalMs ?? SPACE_ADD_PERSIST_BARRIER_POLL_MS;
  const current = getSpaceAddPersistBarrierSnapshot();

  if (
    current.status === "idle" ||
    (current.status === "persisted" &&
      current.targetSpaceIndex === args.targetSpaceIndex)
  ) {
    return Promise.resolve(current);
  }

  if (
    current.targetSpaceIndex !== null &&
    current.targetSpaceIndex !== args.targetSpaceIndex
  ) {
    return Promise.reject(new Error("space_add_persist_target_mismatch"));
  }

  if (current.status === "failed") {
    return Promise.reject(
      new Error(current.error ?? "space_add_persist_failed"),
    );
  }

  if (typeof window === "undefined") {
    return Promise.reject(new Error("space_add_persist_barrier_unavailable"));
  }

  return new Promise((resolve, reject) => {
    let settled = false;
    const startedAt = Date.now();
    let lastActivityAt = current.updatedAt ?? startedAt;

    const pollId = window.setInterval(() => {
      const next = getSpaceAddPersistBarrierSnapshot();
      const now = Date.now();

      if (isSpaceAddPersistSaveAlive(next)) {
        lastActivityAt = now;
      }

      if (now - startedAt >= maxWaitMs) {
        finishReject(new Error("space_add_persist_timeout"));
        return;
      }

      if (now - lastActivityAt >= staleMs) {
        finishResolve(next);
      }
    }, pollIntervalMs);

    const cleanup = () => {
      window.clearInterval(pollId);
      window.removeEventListener(
        SPACE_ADD_PERSIST_BARRIER_CHANGED_EVENT,
        handleChanged,
      );
      args.signal?.removeEventListener("abort", handleAbort);
    };

    const finishResolve = (next: SpaceAddPersistBarrierSnapshot) => {
      if (settled) return;
      settled = true;
      cleanup();
      resolve(next);
    };

    const finishReject = (error: Error) => {
      if (settled) return;
      settled = true;
      cleanup();
      reject(error);
    };

    const handleChanged = () => {
      const next = getSpaceAddPersistBarrierSnapshot();
      if (next.targetSpaceIndex !== args.targetSpaceIndex) {
        if (next.targetSpaceIndex !== null) {
          finishReject(new Error("space_add_persist_target_mismatch"));
        }
        return;
      }
      if (next.status === "persisted") {
        finishResolve(next);
      } else if (next.status === "failed") {
        finishReject(new Error(next.error ?? "space_add_persist_failed"));
      } else {
        lastActivityAt = next.updatedAt ?? Date.now();
      }
    };

    const handleAbort = () => finishReject(new DOMException("Aborted", "AbortError"));

    window.addEventListener(
      SPACE_ADD_PERSIST_BARRIER_CHANGED_EVENT,
      handleChanged,
    );
    args.signal?.addEventListener("abort", handleAbort, { once: true });
    handleChanged();
  });
}
